import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { tokensAPI } from '../../api/tokens';
import { proxiesAPI } from '../../api/proxies';
import { userAPI } from '../../api/user';
import toast from 'react-hot-toast';

const CreateTokenModal = ({ isOpen, onClose, onTokenCreated }) => {
  const { t, i18n } = useTranslation();
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);
  const [limits, setLimits] = useState({
    maxPorts: 3,
    usedPorts: 0,
    availablePorts: 3
  });
  const [availablePortsList, setAvailablePortsList] = useState([]);
  const [showManualInput, setShowManualInput] = useState(false);
  const [portRangesInfo, setPortRangesInfo] = useState(null);
  const [isPortRangesExpanded, setIsPortRangesExpanded] = useState(true);
  
  // Храним ID резерваций для каждого порта
  const [portReservations, setPortReservations] = useState({}); // { "tcp:8080": reservationId }
  
  const [formData, setFormData] = useState({
    proxyId: '',
    portMode: 'auto', // 'auto', 'manual', 'any'
    allowAnyNonPrivileged: false,
    maxPorts: 3,
    duration: '1h',
    ports: []
  });

  // Состояние для генерации команд подключения
  const [connectionConfig, setConnectionConfig] = useState({
    localPort: '3000',
    localIp: '127.0.0.1',
    selectedProtocol: 'tcp',
    selectedPort: null
  });

  // Вычисляем сколько портов будет использовано текущим токеном
  const portsToBeUsed = formData.portMode === 'any'
    ? formData.maxPorts
    : formData.ports.length;
  
  // Вычисляем сколько портов останется доступно после создания токена
  const remainingPorts = limits.availablePorts - portsToBeUsed;
  
  // Проверяем можно ли добавить ещё порт (только для ручного режима)
  const canAddMorePorts = formData.portMode === 'manual' && formData.ports.length < limits.availablePorts;
  
  const [newPort, setNewPort] = useState({
    protocol: 'tcp',
    port: ''
  });
  
  const [errors, setErrors] = useState({});
  const [checkingPort, setCheckingPort] = useState(false);
  const [portError, setPortError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadProxies();
      loadLimits();
    } else {
      // Освобождаем все резервации при закрытии модального окна
      releaseAllReservations();
      
      // Сбрасываем состояние при закрытии
      setCreatedToken(null);
      setFormData({
        proxyId: '',
        portMode: 'auto',
        allowAnyNonPrivileged: false,
        maxPorts: 3,
        duration: '1h',
        ports: []
      });
      setNewPort({ protocol: 'tcp', port: '' });
      setErrors({});
      setPortError('');
      setAvailablePortsList([]);
      setShowManualInput(false);
      setPortRangesInfo(null);
      setIsPortRangesExpanded(true);
      setPortReservations({});
      setConnectionConfig({
        localPort: '3000',
        localIp: '127.0.0.1',
        selectedProtocol: 'tcp',
        selectedPort: null
      });
    }
  }, [isOpen]);

  // Функция для освобождения всех резерваций
  const releaseAllReservations = async () => {
    const reservationIds = Object.values(portReservations);
    if (reservationIds.length > 0) {
      try {
        await tokensAPI.releaseReservations({ reservationIds });
        console.log('Released all reservations:', reservationIds);
      } catch (error) {
        console.error('Error releasing reservations:', error);
      }
    }
  };

  // Сбрасываем конфигурацию подключения при создании нового токена
  useEffect(() => {
    if (createdToken) {
      // Если токен имеет конкретные порты, выбираем первый из них
      if (createdToken.ports && createdToken.ports.length > 0) {
        setConnectionConfig(prev => ({
          ...prev,
          selectedProtocol: createdToken.ports[0].protocol,
          selectedPort: createdToken.ports[0].port
        }));
      }
    }
  }, [createdToken]);

  // Обновляем maxPorts при изменении limits
  useEffect(() => {
    if (isOpen && limits.availablePorts > 0) {
      setFormData(prev => ({
        ...prev,
        maxPorts: Math.min(prev.maxPorts, limits.availablePorts)
      }));
    }
  }, [limits.availablePorts, isOpen]);

  // Автоматически обновляем maxPorts при добавлении/удалении портов (только для ручного режима)
  useEffect(() => {
    if (formData.portMode === 'manual') {
      setFormData(prev => ({
        ...prev,
        maxPorts: Math.max(prev.ports.length, 1) // Минимум 1 порт
      }));
    }
  }, [formData.ports.length, formData.portMode]);

  // Загружаем список доступных портов при изменении прокси или протокола (для ручного режима)
  useEffect(() => {
    if (formData.portMode === 'manual' && formData.proxyId && newPort.protocol) {
      loadAvailablePorts(formData.proxyId, newPort.protocol);
    } else {
      setAvailablePortsList([]);
    }
  }, [formData.proxyId, newPort.protocol, formData.portMode, formData.ports.length]);

  // Загружаем информацию о разрешённых портах при выборе прокси
  useEffect(() => {
    if (formData.proxyId) {
      loadPortRangesInfo(formData.proxyId);
    } else {
      setPortRangesInfo(null);
    }
  }, [formData.proxyId]);

  const loadProxies = async () => {
    try {
      const data = await proxiesAPI.getList({ status: 'online' });
      setProxies(data || []);
      if (data && data.length > 0 && !formData.proxyId) {
        setFormData(prev => ({ ...prev, proxyId: data[0].proxyId }));
      }
    } catch (error) {
      toast.error(t('tokens.createModal.errors.loadProxies'));
    }
  };

  const loadLimits = async () => {
    try {
      const data = await userAPI.getLimits();
      setLimits(data);
    } catch (error) {
      console.error('Error loading limits:', error);
    }
  };

  const loadAvailablePorts = async (proxyId, protocol) => {
    if (!proxyId || !protocol) {
      setAvailablePortsList([]);
      return;
    }

    try {
      // Формируем объект excludePorts из уже добавленных портов
      const excludePortsObj = {};
      for (const port of formData.ports) {
        if (!excludePortsObj[port.protocol]) {
          excludePortsObj[port.protocol] = [];
        }
        excludePortsObj[port.protocol].push(port.port);
      }

      const result = await tokensAPI.findAvailablePorts({
        proxyId,
        protocols: [protocol],
        count: 100, // Загружаем 100 доступных портов
        excludePorts: excludePortsObj
      });

      if (result.ports && Array.isArray(result.ports)) {
        setAvailablePortsList(result.ports);
      } else {
        setAvailablePortsList([]);
      }
    } catch (error) {
      console.error('Error loading available ports:', error);
      setAvailablePortsList([]);
    }
  };

  const loadPortRangesInfo = async (proxyId) => {
    if (!proxyId) {
      setPortRangesInfo(null);
      return;
    }

    try {
      const data = await tokensAPI.getPortRanges(proxyId);
      setPortRangesInfo(data);
    } catch (error) {
      console.error('Error loading port ranges info:', error);
      setPortRangesInfo(null);
    }
  };

  // Форматируем информацию о портах для отображения
  const formatPortRanges = (ranges) => {
    if (!ranges || !Array.isArray(ranges)) return null;

    const result = {};

    for (const range of ranges) {
      const protocol = range.protocol?.toUpperCase() || 'UNKNOWN';
      if (!result[protocol]) {
        result[protocol] = [];
      }
      if (range.ports && Array.isArray(range.ports)) {
        result[protocol].push(...range.ports);
      }
    }

    return result;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Особая обработка для выбора режима портов
    if (name === 'portMode') {
      setFormData(prev => {
        const newState = {
          ...prev,
          portMode: value,
          // Очищаем порты при переключении режима
          ports: [],
          // Сбрасываем allowAnyNonPrivileged
          allowAnyNonPrivileged: value === 'any'
        };

        // При переключении на режим "любые порты" устанавливаем maxPorts = 1 (минимум)
        if (value === 'any' && prev.maxPorts === 0) {
          newState.maxPorts = 1;
        } else if (value === 'any') {
          // Разрешаем использовать все доступные порты, включая текущий лимит
          newState.maxPorts = Math.min(Math.max(prev.maxPorts, 1), limits.availablePorts);
        }

        return newState;
      });
    } else if (name === 'allowAnyNonPrivileged') {
      // Обратная совместимость со старой галочкой
      setFormData(prev => {
        const newState = {
          ...prev,
          allowAnyNonPrivileged: checked,
          portMode: checked ? 'any' : 'manual',
          // Очищаем порты при переключении режима
          ports: []
        };

        // При включении режима "любые порты" устанавливаем maxPorts = 1 (минимум)
        if (checked && prev.maxPorts === 0) {
          newState.maxPorts = 1;
        } else if (checked) {
          // Разрешаем использовать все доступные порты, включая текущий лимит
          newState.maxPorts = Math.min(Math.max(prev.maxPorts, 1), limits.availablePorts);
        }

        return newState;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddPort = async () => {
    if (!canAddMorePorts) {
      toast.error(t('tokens.createModal.toasts.limitReached'));
      return;
    }

    if (!newPort.port) {
      toast.error(showManualInput ? t('tokens.createModal.toasts.enterPortNumber') : t('tokens.createModal.toasts.selectPortFromList'));
      return;
    }

    const portNum = parseInt(newPort.port);
    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
      toast.error(t('tokens.createModal.toasts.invalidPortNumber'));
      return;
    }

    if (formData.ports.some(p => p.port === portNum && p.protocol === newPort.protocol)) {
      toast.error(t('tokens.createModal.toasts.portAlreadyAdded'));
      return;
    }

    setCheckingPort(true);
    setPortError('');

    try {
      // Резервируем порт вместо простой проверки
      const reservationResult = await tokensAPI.reservePorts({
        proxyId: formData.proxyId,
        ports: [{
          protocol: newPort.protocol,
          port: portNum
        }]
      });

      if (reservationResult.reservations && reservationResult.reservations.length > 0) {
        const reservation = reservationResult.reservations[0];
        
        // Добавляем порт с reservationId
        setFormData(prev => ({
          ...prev,
          ports: [...prev.ports, { protocol: newPort.protocol, port: portNum }]
        }));
        
        // Сохраняем reservationId
        setPortReservations(prev => ({
          ...prev,
          [`${newPort.protocol}:${portNum}`]: reservation.id
        }));
        
        setNewPort(prev => ({ ...prev, port: '' }));
        setPortError('');
        
        toast.success(t('tokens.createModal.toasts.portReserved', { 
          protocol: newPort.protocol.toUpperCase(), 
          port: portNum 
        }));
      }
    } catch (error) {
      console.error('Error reserving port:', error);
      
      // Если ошибка 409 - порт занят
      if (error.response?.status === 409 || error.status === 409) {
        const conflictPorts = error.data?.conflicts?.map(c =>
          `${c.protocol.toUpperCase()}:${c.port}`
        ).join(', ') || `${newPort.protocol.toUpperCase()}:${portNum}`;
        
        setPortError(t('tokens.createModal.errors.portTaken', { port: conflictPorts }));
        toast.error(t('tokens.createModal.toasts.portTaken', { port: conflictPorts }));
      } else {
        toast.error(t('tokens.createModal.errors.checkPortError'));
      }
    } finally {
      setCheckingPort(false);
    }
  };

  const handleRemovePort = async (index) => {
    const port = formData.ports[index];
    const portKey = `${port.protocol}:${port.port}`;
    const reservationId = portReservations[portKey];

    // Освобождаем резервацию
    if (reservationId) {
      try {
        await tokensAPI.deleteReservation(reservationId);
        console.log('Released reservation:', reservationId);
      } catch (error) {
        console.error('Error releasing reservation:', error);
        // Продолжаем даже если не удалось освободить
      }
    }

    // Удаляем порт из списка
    setFormData(prev => ({
      ...prev,
      ports: prev.ports.filter((_, i) => i !== index)
    }));

    // Удаляем reservationId из состояния
    setPortReservations(prev => {
      const newReservations = { ...prev };
      delete newReservations[portKey];
      return newReservations;
    });
  };

  const handleAutoAddPorts = async (protocol) => {
    if (!formData.proxyId) {
      toast.error(t('tokens.createModal.toasts.selectProxyFirst'));
      return;
    }

    if (formData.ports.length >= limits.availablePorts) {
      toast.error(t('tokens.createModal.toasts.limitReachedAuto'));
      return;
    }

    const countToFind = Math.min(1, limits.availablePorts - formData.ports.length);
    setCheckingPort(true);
    setPortError('');

    try {
      // Формируем объект excludePorts в правильном формате
      const excludePortsObj = {};
      for (const port of formData.ports) {
        if (!excludePortsObj[port.protocol]) {
          excludePortsObj[port.protocol] = [];
        }
        excludePortsObj[port.protocol].push(port.port);
      }

      const result = await tokensAPI.findAvailablePorts({
        proxyId: formData.proxyId,
        protocols: [protocol],
        count: countToFind,
        excludePorts: excludePortsObj
      });

      if (result.ports && result.ports.length > 0) {
        const foundPort = result.ports[0];
        
        // Проверяем, что найденный порт не дублирует уже добавленные
        const isDuplicate = formData.ports.some(p => p.protocol === foundPort.protocol && p.port === foundPort.port);

        if (isDuplicate) {
          toast.error(t('tokens.createModal.errors.portAlreadyAdded', { protocol: protocol.toUpperCase(), port: foundPort.port }));
          setCheckingPort(false);
          return;
        }

        // Резервируем найденный порт
        try {
          const reservationResult = await tokensAPI.reservePorts({
            proxyId: formData.proxyId,
            ports: [foundPort]
          });

          if (reservationResult.reservations && reservationResult.reservations.length > 0) {
            const reservation = reservationResult.reservations[0];
            
            // Добавляем порт с reservationId
            setFormData(prev => ({
              ...prev,
              ports: [...prev.ports, foundPort]
            }));
            
            // Сохраняем reservationId
            setPortReservations(prev => ({
              ...prev,
              [`${foundPort.protocol}:${foundPort.port}`]: reservation.id
            }));
            
            toast.success(t('tokens.createModal.toasts.portReserved', { 
              protocol: protocol.toUpperCase(), 
              port: foundPort.port 
            }));
          }
        } catch (reserveError) {
          console.error('Error reserving found port:', reserveError);
          toast.error(t('tokens.createModal.toasts.portTaken', { 
            port: `${foundPort.protocol.toUpperCase()}:${foundPort.port}` 
          }));
        }
      } else {
        toast.error(t('tokens.createModal.toasts.noFreePortFound', { protocol: protocol.toUpperCase() }));
      }
    } catch (error) {
      console.error('Error finding available ports:', error);
      toast.error(t('tokens.createModal.errors.findPortsError'));
    } finally {
      setCheckingPort(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.proxyId) {
      newErrors.proxyId = t('tokens.createModal.errors.proxyRequired');
    }

    if (formData.portMode === 'manual' && formData.ports.length === 0) {
      newErrors.ports = t('tokens.createModal.errors.portsRequired');
    }

    if (formData.maxPorts <= 0) {
      newErrors.maxPorts = t('tokens.createModal.errors.maxPortsMustBePositive');
    }

    // Проверяем превышение лимита портов
    if (remainingPorts < 0) {
      newErrors.maxPorts = t('tokens.createModal.limits.exceededLimit', { 
        used: limits.usedPorts, 
        max: limits.maxPorts, 
        count: portsToBeUsed 
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const requestData = {
        proxyId: formData.proxyId,
        duration: formData.duration
      };

      if (formData.portMode === 'any') {
        // В режиме "любые порты" используем maxPorts из формы
        requestData.maxPorts = formData.maxPorts;
        requestData.allowAnyNonPrivileged = true;
      } else {
        // В режимах "auto" и "manual" maxPorts должен равняться количеству добавленных портов
        requestData.maxPorts = formData.ports.length;
        requestData.ports = formData.ports;
        
        // Добавляем reservationIds для зарезервированных портов
        const reservationIds = formData.ports
          .map(port => portReservations[`${port.protocol}:${port.port}`])
          .filter(id => id !== undefined);
        
        if (reservationIds.length > 0) {
          requestData.reservationIds = reservationIds;
        }
      }

      const result = await tokensAPI.create(requestData);
      setCreatedToken(result);
      
      // Очищаем резервации после успешного создания токена
      setPortReservations({});
      
      toast.success(t('tokens.createModal.toasts.created'));

      // Обновляем лимиты после создания токена
      await loadLimits();

      if (onTokenCreated) {
        onTokenCreated();
      }
    } catch (error) {
      // Ошибка уже обрабатывается в axios interceptor и показывается через toast
      console.error('Error creating token:', error);
      
      // Если ошибка связана с резервациями - очищаем их
      if (error.response?.status === 400 && error.response?.data?.missingReservationIds) {
        setPortReservations({});
        setFormData(prev => ({ ...prev, ports: [] }));
        toast.error(t('tokens.createModal.errors.reservationsExpired'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCreatedToken(null);
    setConnectionConfig({
      localPort: '3000',
      localIp: '127.0.0.1',
      selectedProtocol: 'tcp',
      selectedPort: null
    });
    onClose();
  };

  const copyToken = () => {
    navigator.clipboard.writeText(createdToken.token);
    toast.success(t('tokens.createModal.success.copied'));
  };

  // Получаем IP адрес прокси сервера
  const getProxyHost = () => {
    const proxy = proxies.find(p => p.proxyId === createdToken?.proxy_id);
    return proxy?.ipAddress || '84.247.129.232';
  };

  // Генерируем строку проброса портов
  const generateForwardString = () => {
    if (!connectionConfig.selectedPort) return '';
    return `${connectionConfig.selectedProtocol}:${connectionConfig.selectedPort}:${connectionConfig.localIp}:${connectionConfig.localPort}`;
  };

  // Генерируем команду для Windows
  const generateWindowsCommand = () => {
    const host = getProxyHost();
    const forward = generateForwardString();
    return `port2port.exe -host ${host} -token "${createdToken?.token || 'TOKEN'}" -forwards "${forward}"`;
  };

  // Генерируем команду для Linux
  const generateLinuxCommand = () => {
    const host = getProxyHost();
    const forward = generateForwardString();
    return `./port2port -host ${host} -token "${createdToken?.token || 'TOKEN'}" -forwards "${forward}"`;
  };

  // Копирование команды
  const copyCommand = (command) => {
    navigator.clipboard.writeText(command);
    toast.success(t('tokens.createModal.success.commandCopied'));
  };

  // Обработчик изменения конфигурации подключения
  const handleConnectionConfigChange = (field, value) => {
    setConnectionConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={createdToken ? t('tokens.createModal.titleCreated') : t('tokens.createModal.title')}
      size="lg"
    >
      {createdToken ? (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-green-800">
                {t('tokens.createModal.success.message')}
              </p>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('tokens.card.title')}
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                readOnly
                value={createdToken.token}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
              />
              <Button onClick={copyToken} size="sm">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {t('tokens.createModal.copyToken')}
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
              </svg>
              <span className="text-gray-600">{t('tokens.card.proxy')}:</span>
              <span className="font-medium">
                {(() => {
                  const proxy = proxies.find(p => p.proxyId === createdToken?.proxy_id);
                  return proxy?.countryCode
                    ? `${proxy.countryCode} - ${proxy.name || proxy.proxyId}`
                    : (createdToken?.proxy_id || '');
                })()}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gray-600">{t('tokens.createModal.expires')}:</span>
              <span className="font-medium">
                {createdToken.unlimited || (createdToken.expires_at && new Date(createdToken.expires_at).getFullYear() >= 9999) ? (
                  <span className="text-green-600">{t('tokens.createModal.unlimited')}</span>
                ) : (
                  new Date(createdToken.expires_at).toLocaleString(i18n.language)
                )}
              </span>
            </div>
          </div>

          {/* Секция генерации команд подключения */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              {t('tokens.createModal.connectionCommand')}
            </h3>
            
            {/* Конфигурация подключения */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('tokens.createModal.localAppSettings')}
              </label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Локальный IP */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('tokens.createModal.localIp')}</label>
                  <input
                    type="text"
                    value={connectionConfig.localIp}
                    onChange={(e) => handleConnectionConfigChange('localIp', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="127.0.0.1"
                  />
                </div>
                
                {/* Локальный порт */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('tokens.createModal.localPort')}</label>
                  <input
                    type="number"
                    value={connectionConfig.localPort}
                    onChange={(e) => handleConnectionConfigChange('localPort', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="3000"
                    min="1"
                    max="65535"
                  />
                </div>
              </div>

              {/* Выбор порта на прокси (если есть несколько портов) */}
              {createdToken.ports && createdToken.ports.length > 1 && (
                <div className="mt-3">
                  <label className="block text-xs text-gray-600 mb-1">{t('tokens.createModal.proxyPort')}</label>
                  <select
                    value={`${connectionConfig.selectedProtocol}:${connectionConfig.selectedPort}`}
                    onChange={(e) => {
                      const [protocol, port] = e.target.value.split(':');
                      setConnectionConfig(prev => ({
                        ...prev,
                        selectedProtocol: protocol,
                        selectedPort: parseInt(port)
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {createdToken.ports.map((port, idx) => (
                      <option key={idx} value={`${port.protocol}:${port.port}`}>
                        {port.protocol.toUpperCase()}:{port.port}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <p className="mt-2 text-xs text-gray-500">
                {t('tokens.createModal.localAppHint')}
              </p>
            </div>

            {/* Команды для разных ОС */}
            <div className="space-y-3">
              {/* Windows */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                  </svg>
                  {t('tokens.createModal.windows')}
                </label>
                <div className="flex space-x-2">
                  <code className="flex-1 px-3 py-2 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-x-auto">
                    {generateWindowsCommand()}
                  </code>
                  <Button
                    onClick={() => copyCommand(generateWindowsCommand())}
                    size="sm"
                    variant="secondary"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {t('tokens.createModal.copyCommand')}
                  </Button>
                </div>
              </div>

              {/* Linux */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.004 0c-6.623 0-10 4.627-10 9 0 3.261 1.765 5.743 4.531 7.198-.126.946-.383 2.422-.795 3.38-.194.456.08.905.566.836 1.936-.276 3.908-1.426 4.738-2.055.314.045.639.07.971.07.621 0 1.221-.058 1.793-.168 1.578 1.095 3.768 2.394 5.895 2.697.486.069.76-.38.566-.836-.412-.958-.669-2.434-.795-3.38C22.239 14.743 24 12.261 24 9c0-4.373-3.377-9-10-9zm0 2c4.947 0 8 3.738 8 7 0 2.585-1.316 4.622-3.531 5.828.016.157.033.315.053.472.098.762.258 1.637.464 2.466-1.36-.247-2.749-.988-3.779-1.746-.419.082-.851.137-1.291.162-.39.022-.783.033-1.18.033-.323 0-.639-.025-.947-.073-1.03.758-2.419 1.499-3.779 1.746.206-.829.366-1.704.464-2.466.02-.157.037-.315.053-.472C3.316 13.622 2 11.585 2 9c0-3.262 3.053-7 8-7z"/>
                  </svg>
                  {t('tokens.createModal.linux')}
                </label>
                <div className="flex space-x-2">
                  <code className="flex-1 px-3 py-2 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-x-auto">
                    {generateLinuxCommand()}
                  </code>
                  <Button
                    onClick={() => copyCommand(generateLinuxCommand())}
                    size="sm"
                    variant="secondary"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {t('tokens.createModal.copyCommand')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Инструкция */}
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <span className="font-semibold">{t('tokens.createModal.instruction')}</span>
              </p>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <Button onClick={handleClose}>
              {t('tokens.createModal.close')}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Информация о лимитах */}
          <div className={`p-4 rounded-lg border ${
            remainingPorts < 0
              ? 'bg-red-50 border-red-200'
              : remainingPorts === 0
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
               <span className="text-sm font-medium text-gray-700">
                   {remainingPorts >= 0
                     ? t('tokens.createModal.limits.availablePorts', { available: remainingPorts, max: limits.maxPorts })
                     : t('tokens.createModal.limits.exceeded', { count: Math.abs(remainingPorts) })
                   }
                 </span>
              </div>
              <span className="text-sm text-gray-600">
                {t('tokens.createModal.limits.used', { used: limits.usedPorts })}{portsToBeUsed > 0 && t('tokens.createModal.limits.willBeUsed', { count: portsToBeUsed })}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  remainingPorts < 0
                    ? 'bg-red-600'
                    : remainingPorts === 0
                    ? 'bg-yellow-500'
                    : 'bg-blue-600'
                }`}
                style={{ width: `${Math.min(((limits.maxPorts - remainingPorts) / limits.maxPorts) * 100, 100)}%` }}
              ></div>
            </div>
            {remainingPorts < 0 && (
              <p className="mt-2 text-sm text-red-800">
                {t('tokens.createModal.limits.exceededWarning', { used: limits.usedPorts, max: limits.maxPorts, count: portsToBeUsed })}
              </p>
            )}
            {remainingPorts === 0 && portsToBeUsed > 0 && (
              <p className="mt-2 text-sm text-yellow-800">
                {t('tokens.createModal.limits.allUsedWarning')}
              </p>
            )}
          </div>

          {/* Выбор прокси */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center space-x-1">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
                <span>{t('tokens.createModal.proxy')} *</span>
              </div>
            </label>
            <select
              name="proxyId"
              value={formData.proxyId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('tokens.createModal.proxyRequired')}</option>
              {proxies.map(proxy => (
                <option key={proxy.proxyId} value={proxy.proxyId}>
                  {proxy.countryCode ? `${proxy.countryCode} - ` : ''}{proxy.name || proxy.proxyId} ({proxy.status})
                </option>
              ))}
            </select>
            {errors.proxyId && (
              <p className="mt-1 text-sm text-red-600">{errors.proxyId}</p>
            )}
          </div>

          {/* Режим выбора портов */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center space-x-1">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{t('tokens.createModal.mode')}</span>
              </div>
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => handleInputChange({ target: { name: 'portMode', value: 'auto' } })}
                className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  formData.portMode === 'auto'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="flex flex-col items-center space-y-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>{t('tokens.createModal.modeAuto')}</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleInputChange({ target: { name: 'portMode', value: 'manual' } })}
                className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  formData.portMode === 'manual'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="flex flex-col items-center space-y-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>{t('tokens.createModal.modeManual')}</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleInputChange({ target: { name: 'portMode', value: 'any' } })}
                className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  formData.portMode === 'any'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="flex flex-col items-center space-y-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                  <span>{t('tokens.createModal.modeAny')}</span>
                </div>
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {formData.portMode === 'manual' && t('tokens.createModal.modeManualDesc')}
              {formData.portMode === 'auto' && t('tokens.createModal.modeAutoDesc')}
              {formData.portMode === 'any' && t('tokens.createModal.modeAnyDesc')}
            </p>
          </div>

          {/* Порты (если не включён режим "любые порты") */}
          {formData.portMode !== 'any' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-1">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>{t('tokens.createModal.ports')} *</span>
                </div>
              </label>

              {/* Режим: Автоматическое добавление */}
              {formData.portMode === 'auto' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      type="button"
                      onClick={() => handleAutoAddPorts('tcp')}
                      variant="success"
                      disabled={!formData.proxyId || checkingPort || formData.ports.length >= limits.availablePorts}
                      className="w-full"
                    >
                      {t('tokens.createModal.addTcp')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => handleAutoAddPorts('udp')}
                      variant="success"
                      disabled={!formData.proxyId || checkingPort || formData.ports.length >= limits.availablePorts}
                      className="w-full"
                    >
                      {t('tokens.createModal.addUdp')}
                    </Button>
                  </div>
                  {checkingPort && (
                    <p className="text-sm text-gray-600 text-center">{t('tokens.createModal.searchingPort')}</p>
                  )}
                </div>
              )}

              {/* Режим: Ручной ввод */}
              {formData.portMode === 'manual' && (
                <div className="space-y-3">
                   {/* Информация о разрешённых портах */}
                   {portRangesInfo && (
                     <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                       {/* Заголовок с кнопкой сворачивания */}
                       <button
                         type="button"
                         onClick={() => setIsPortRangesExpanded(!isPortRangesExpanded)}
                         className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-blue-100 transition-colors"
                       >
                         <div className="flex items-center space-x-2">
                           <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                           </svg>
                           <span className="text-sm font-medium text-blue-800">{t('tokens.createModal.allowedPorts')}</span>
                         </div>
                         <svg
                           className={`w-4 h-4 text-blue-600 transition-transform ${isPortRangesExpanded ? 'rotate-180' : ''}`}
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                         >
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                         </svg>
                       </button>

                       {/* Контент (сворачиваемый) */}
                       {isPortRangesExpanded && (
                         <div className="px-3 pb-3">
                           <div className="text-xs text-blue-800 space-y-2">
                             {portRangesInfo.proxy?.allowedPorts && formatPortRanges(portRangesInfo.proxy.allowedPorts) ? (
                               <div>
                                 <div className="font-medium mb-1">{t('tokens.createModal.onProxy')}</div>
                                 <div className="space-y-1 pl-2">
                                   {Object.entries(formatPortRanges(portRangesInfo.proxy.allowedPorts)).map(([protocol, ports]) => (
                                     <div key={protocol}>
                                       <span className="font-medium">{protocol}:</span> {ports.join(', ')}
                                     </div>
                                   ))}
                                 </div>
                               </div>
                             ) : (
                               <div>{t('tokens.createModal.onProxy')} {t('tokens.createModal.allPorts')}</div>
                             )}
                             {portRangesInfo.userOverrides && portRangesInfo.userOverrides.length > 0 && (
                               <div className="pt-2 border-t border-blue-300">
                                 <div className="font-medium mb-1">{t('tokens.createModal.yourOverrides')}</div>
                                 <div className="space-y-1 pl-2">
                                   {portRangesInfo.userOverrides.map((override, idx) => {
                                     const formatted = formatPortRanges([override]);
                                     if (!formatted) return null;
                                     return (
                                       <div key={idx}>
                                         {Object.entries(formatted).map(([protocol, ports]) => (
                                           <div key={protocol}>
                                             <span className="font-medium">{protocol}:</span> {ports.join(', ')}
                                             {override.proxyId && <span className="text-blue-600 ml-1">{t('tokens.createModal.onlyForProxy', { proxyId: override.proxyId })}</span>}
                                           </div>
                                         ))}
                                       </div>
                                     );
                                   })}
                                 </div>
                               </div>
                             )}
                           </div>
                         </div>
                       )}
                     </div>
                   )}

                   {/* Переключатель режимов ввода */}
                   <div className="flex items-center space-x-2 text-sm">
                     <button
                       type="button"
                       onClick={() => setShowManualInput(false)}
                       className={`px-3 py-1 rounded-lg transition-all ${
                         !showManualInput
                           ? 'bg-blue-500 text-white'
                           : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                       }`}
                     >
                       {t('tokens.createModal.fromList')}
                     </button>
                     <button
                       type="button"
                       onClick={() => setShowManualInput(true)}
                       className={`px-3 py-1 rounded-lg transition-all ${
                         showManualInput
                           ? 'bg-blue-500 text-white'
                           : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                       }`}
                     >
                       {t('tokens.createModal.manual')}
                     </button>
                   </div>

                   {!showManualInput ? (
                     // Режим выбора из списка
                     <div className="space-y-2">
                       <div className="flex space-x-2">
                         <select
                           value={newPort.protocol}
                           onChange={(e) => {
                             setNewPort(prev => ({ ...prev, protocol: e.target.value, port: '' }));
                             setPortError('');
                           }}
                           className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                         >
                           <option value="tcp">TCP</option>
                           <option value="udp">UDP</option>
                         </select>
                         <select
                           value={newPort.port}
                           onChange={(e) => {
                             setNewPort(prev => ({ ...prev, port: e.target.value }));
                             setPortError('');
                           }}
                           disabled={!formData.proxyId || availablePortsList.length === 0}
                           className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                             portError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                           } ${!formData.proxyId || availablePortsList.length === 0 ? 'bg-gray-100 text-gray-400' : ''}`}
                         >
                           <option value="">{!formData.proxyId ? t('tokens.createModal.firstSelectProxy') : availablePortsList.length === 0 ? t('tokens.createModal.noAvailablePorts') : t('tokens.createModal.selectPort')}</option>
                           {availablePortsList.map((port, idx) => (
                             <option key={idx} value={port.port}>
                               {port.port}
                             </option>
                           ))}
                         </select>
                         <Button
                           type="button"
                           onClick={handleAddPort}
                           variant="success"
                           size="sm"
                           disabled={!canAddMorePorts || !newPort.port}
                         >
                           {t('tokens.createModal.addPort')}
                         </Button>
                       </div>
                       {availablePortsList.length === 0 && formData.proxyId && newPort.protocol && (
                         <p className="text-xs text-gray-500">{t('tokens.createModal.tryManualInput')}</p>
                       )}
                       {availablePortsList.length > 0 && (
                         <p className="text-xs text-gray-500">{t('tokens.createModal.availablePortsCount', { count: availablePortsList.length })}</p>
                       )}
                     </div>
                   ) : (
                     // Режим ручного ввода
                     <div className="flex space-x-2">
                       <select
                         value={newPort.protocol}
                         onChange={(e) => {
                           setNewPort(prev => ({ ...prev, protocol: e.target.value, port: '' }));
                           setPortError('');
                         }}
                         className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                       >
                         <option value="tcp">TCP</option>
                         <option value="udp">UDP</option>
                       </select>
                       <input
                         type="number"
                         value={newPort.port}
                         onChange={(e) => {
                           setNewPort(prev => ({ ...prev, port: e.target.value }));
                           setPortError('');
                         }}
                         placeholder={t('tokens.createModal.portNumber')}
                         min="1"
                         max="65535"
                         className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                           portError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                         }`}
                       />
                       <Button
                         type="button"
                         onClick={handleAddPort}
                         variant="success"
                         size="sm"
                         disabled={!canAddMorePorts || !newPort.port || checkingPort}
                       >
                         {checkingPort ? t('tokens.createModal.checkingPort') : t('tokens.createModal.addPort')}
                       </Button>
                     </div>
                   )}
                </div>
              )}

              {portError && (
                <p className="mt-1 text-sm text-red-600">{portError}</p>
              )}

              {formData.ports.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.ports.map((port, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                    >
                      {port.protocol}/{port.port}
                      <button
                        type="button"
                        onClick={() => handleRemovePort(index)}
                        className="ml-1 hover:text-blue-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

               <p className="mt-2 text-xs text-gray-500">
                 {t('tokens.createModal.addedPortsCount', { added: formData.ports.length, available: limits.availablePorts })}
                 {formData.ports.length >= limits.availablePorts && (
                   <span className="text-yellow-600 font-medium"> ({t('tokens.createModal.limitReached')})</span>
                 )}
               </p>

              {errors.ports && (
                <p className="mt-1 text-sm text-red-600">{errors.ports}</p>
              )}
            </div>
          )}

          {/* Максимальное количество портов (только для режима "любые порты") */}
           {formData.portMode === 'any' && (
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">
                 <div className="flex items-center space-x-1">
                   <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                   </svg>
                   <span>{t('tokens.createModal.maxPorts')} *</span>
                 </div>
               </label>
               <input
                 type="number"
                 name="maxPorts"
                 value={formData.maxPorts}
                 onChange={handleInputChange}
                 min="1"
                 max={limits.availablePorts}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                 required
                 disabled={limits.availablePorts === 0}
               />
               <p className="mt-1 text-xs text-gray-500">
                 {remainingPorts >= 0
                   ? t('tokens.createModal.limits.selectMaxPorts', { available: limits.availablePorts, remaining: remainingPorts })
                   : t('tokens.createModal.limits.exceededLimit', { used: limits.usedPorts, max: limits.maxPorts, count: formData.maxPorts })
                 }
               </p>
               {errors.maxPorts && (
                 <p className="mt-1 text-sm text-red-600">{errors.maxPorts}</p>
               )}
             </div>
           )}

           {/* Длительность */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
               <div className="flex items-center space-x-1">
                 <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                 </svg>
                 <span>{t('tokens.createModal.duration')}</span>
               </div>
             </label>
             <select
               name="duration"
               value={formData.duration}
               onChange={handleInputChange}
               className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
             >
               <option value="unlimited">⏰ {t('tokens.createModal.durations.unlimited')}</option>
               <option value="30m">{t('tokens.createModal.durations.30m')}</option>
               <option value="1h">{t('tokens.createModal.durations.1h')}</option>
               <option value="6h">{t('tokens.createModal.durations.6h')}</option>
               <option value="24h">{t('tokens.createModal.durations.24h')}</option>
               <option value="7d">{t('tokens.createModal.durations.7d')}</option>
               <option value="30d">{t('tokens.createModal.durations.30d')}</option>
             </select>
             {formData.duration === 'unlimited' && (
               <p className="mt-1 text-xs text-gray-500">
                 {t('tokens.createModal.durations.unlimitedWarning')}
               </p>
             )}
           </div>

           {/* Кнопки */}
           <div className="flex justify-end space-x-3 pt-4">
             <Button
               type="button"
               variant="ghost"
               onClick={handleClose}
               disabled={loading}
             >
               {t('tokens.createModal.cancel')}
             </Button>
             <Button
               type="submit"
               variant="primary"
               loading={loading}
               disabled={loading || remainingPorts < 0 || limits.availablePorts === 0}
             >
               
               {t('tokens.createModal.create')}
             </Button>
           </div>
         </form>
       )}
    </Modal>
  );
};

export default CreateTokenModal;
